import { jsxs, jsx } from 'react/jsx-runtime';
import { createContext, useContext, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { setRef } from './ref.js';
import { HAS_WINDOW } from './window.js';
import { persistentPopupOutlet } from './persistent-popup-outlet.css.js';

/**
 * A set of all registered Popup obstacles.
 *
 * As a popup is dragged around the screen, it will collide with registered Popup obstacles.
 */
const POPUP_OBSTACLES = new Set();
/**
 * Defaults to `document.body` mostly as backwards compatibility for existing tests that don't set
 * up a `PersistentPopupOutletProvider`. When the provider is used, it initially sets the context to `null`
 * and then updates it to the actual outlet element after rendering.
 */
const PersistentPopupOutletContext = createContext(HAS_WINDOW ? document.body : null);
/**
 * Get a list of elements that have registered themselves as obstacles using the
 * `useRegisterPopupObstacle` hook.
 *
 * @note This returns a new array on each call. Please let us know if you need to be able to respond
 *   to changes to the registered Popup obstacles. For FPL's use case, we only need to know about
 *   registered Popup obstacles imperatively.
 */
function getPopupObstacles() {
    return Array.from(POPUP_OBSTACLES);
}
/**
 * Get the element that windows should be rendered into via React portal.
 *
 * @note This hook may return null if it is called before the `PersistentPopupOutletProvider`
 * has rendered and set the context value. If you expect the outlet to already be present,
 * you should use `usePersistentPopupOutlet` instead.
 */
function usePersistentPopupOutletOrNull() {
    return useContext(PersistentPopupOutletContext);
}
/**
 * Get the element that Popups should be rendered into via React portal.
 *
 * @note It is an error to call this hook before the `PersistentPopupOutletProvider` has been rendered. This
 * means it is safe to call this from a hook inside of the Popup itself, because if the Popup was
 * able to render, then the `PersistentPopupOutletProvider` must have been rendered.
 */
function usePersistentPopupOutlet() {
    const outlet = usePersistentPopupOutletOrNull();
    if (outlet == null) {
        throw new Error('Attempted to use PersistentPopupOutlet before it was rendered. If you need to use this hook before ' +
            'the PersistentPopupOutletProvider has rendered, use `usePersistentPopupOutletOrNull` instead.');
    }
    return outlet;
}
/**
 * Renders its children into the Popup outlet, a `<div>` that is rendered to the page by
 * `PersistentPopupOutletProvider` and is intended to be the parent of all floating windows. This
 * allows us to render Popups at a consistent z-index relative to other elements, while allowing
 * Popups to dynamically change their own z-index in relation to other Popups.
 */
function PersistentPopupOutlet({ children }) {
    const outlet = usePersistentPopupOutletOrNull();
    return outlet ? createPortal(children, outlet) : null;
}
PersistentPopupOutlet.displayName = 'PersistentPopupOutlet';
/**
 * Wrap the root of your React app in this component at the point where you want Popup elements
 * to be rendered. This renders a `<div>` as a sibling of the passed in `children` that is used
 * by the `<PersistentPopupOutlet>` component.
 */
function PersistentPopupOutletProvider({ children }) {
    const [element, setElement] = useState(null);
    return (jsxs(PersistentPopupOutletContext.Provider, { value: element, children: [children, jsx("div", { className: persistentPopupOutlet, ref: setElement })] }));
}
PersistentPopupOutletProvider.displayName = 'PersistentPopupOutletProvider';
/**
 * Set an explicit container for the Popup outlet, either by passing in a ref to an existing
 * element, or a string that will be used to query for the element using `document.querySelector`.
 *
 * This can be used to render a Popup in a separate stacking context from other Popups, perhaps
 * to layer it behind other elements that the main PersistentPopupOutletProvider would normally render above.
 *
 * It can also be helpful in contexts where it isn't possible to wrap the whole app in a context
 * provider, such as an astro site.
 *
 * @note It is recommended that the element passed in uses similar styles to the default
 *   `PersistentPopupOutletProvider`:
 *
 *    ```css
 *    .popupOutlet {
 *      position: fixed;
 *      inset: 0;
 *      isolation: isolate;
 *      pointer-events: none;
 *      // Feel free to use a different z-index
 *      z-index: 9;
 *
 *      & > * {
 *        pointer-events: auto;
 *      }
 *    }
 *    ```
 */
function CustomPersistentPopupOutletProvider({ children, container, }) {
    const [element, setElement] = useState(null);
    useLayoutEffect(() => {
        const el = typeof container === 'string' ? document.querySelector(container) : container;
        if (el instanceof HTMLElement) {
            setElement(el);
        }
    }, [container]);
    return (jsx(PersistentPopupOutletContext.Provider, { value: element, children: children }));
}
CustomPersistentPopupOutletProvider.displayName = 'CustomPersistentPopupOutletProvider';
/**
 * Register an element as a window obstacle. Popup obstacles are elements that should act as
 * collision edges while moving windows around. They can also be used to implement avoidance logic
 * for floating tooltips or dropdowns.
 *
 * In Figma, it usually makes sense for any self-contained and long-lived floating element to be
 * registered as a window obstacle (e.g. the layers panel, properties panel, toolbar).
 *
 * @param refIn An existing ref passed to your component. This hook returns a new ref that should
 *   be passed to the element you want to register as a window obstacle - this parameter allows
 *   merging refs together.
 *
 * @returns A ref callback that should be passed to the element you want to register as a window.
 */
function useRegisterPopupObstacle(refIn) {
    const storedElement = useRef(null);
    return useCallback((el) => {
        setRef(refIn, el);
        if (el == null) {
            POPUP_OBSTACLES.delete(storedElement.current);
        }
        else {
            POPUP_OBSTACLES.add(el);
        }
        storedElement.current = el;
    }, [refIn]);
}

export { CustomPersistentPopupOutletProvider, PersistentPopupOutlet, PersistentPopupOutletProvider, getPopupObstacles, usePersistentPopupOutlet, usePersistentPopupOutletOrNull, useRegisterPopupObstacle };
//# sourceMappingURL=persistent-popup-outlet.js.map
